import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Edit2, X, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageBookingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editData, setEditData] = useState({
    date: '',
    time: '',
    guests: '',
    special_requests: ''
  });

  useEffect(() => {
    fetchBooking();
  }, [token]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/manage/${token}`);
      setBooking(response.data);
      setEditData({
        date: response.data.date,
        time: response.data.time,
        guests: response.data.guests,
        special_requests: response.data.special_requests || ''
      });
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Booking not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleModify = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/bookings/manage/${token}`, editData);
      setBooking(response.data);
      setEditing(false);
      toast.success('Reservation updated successfully!');
    } catch (error) {
      console.error('Error modifying booking:', error);
      toast.error('Failed to update reservation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/bookings/manage/${token}/cancel`, { reason: cancelReason });
      setBooking({ ...booking, status: 'cancelled' });
      setCancelling(false);
      toast.success('Reservation cancelled successfully');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel reservation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="manage-booking-page">
        <Loader2 className="w-8 h-8 animate-spin text-malva" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen pt-32 bg-gradient-to-b from-silver-bright to-white" data-testid="manage-booking-page">
        <div className="container-elegant text-center">
          <h1 className="font-serif text-3xl text-gray-800 mb-4">Booking Not Found</h1>
          <p className="text-gray-500 mb-8">This booking link may have expired or is invalid.</p>
          <Button onClick={() => navigate('/booking')} className="btn-primary">
            Make a New Reservation
          </Button>
        </div>
      </div>
    );
  }

  const isCancelled = booking.status === 'cancelled';

  return (
    <div className="min-h-screen pt-28 pb-16 bg-gradient-to-b from-silver-bright to-white" data-testid="manage-booking-page">
      <div className="container-elegant max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl md:text-4xl text-gray-800 mb-2">Manage Your Reservation</h1>
          <p className="text-gray-500">Confirmation #{booking.id.slice(0, 8).toUpperCase()}</p>
        </div>

        {isCancelled ? (
          <div className="card-elegant p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-serif text-2xl text-gray-800 mb-2">Reservation Cancelled</h2>
            <p className="text-gray-500 mb-6">This reservation has been cancelled.</p>
            <Button onClick={() => navigate('/booking')} className="btn-primary">
              Make a New Reservation
            </Button>
          </div>
        ) : editing ? (
          <div className="card-elegant p-8">
            <h2 className="font-serif text-xl text-gray-800 mb-6">Edit Reservation</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={editData.date}
                    onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <select
                    value={editData.time}
                    onChange={(e) => setEditData({ ...editData, time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-malva"
                  >
                    <option value="12:00">12:00 PM</option>
                    <option value="12:30">12:30 PM</option>
                    <option value="13:00">1:00 PM</option>
                    <option value="13:30">1:30 PM</option>
                    <option value="17:00">5:00 PM</option>
                    <option value="17:30">5:30 PM</option>
                    <option value="18:00">6:00 PM</option>
                    <option value="18:30">6:30 PM</option>
                    <option value="19:00">7:00 PM</option>
                    <option value="19:30">7:30 PM</option>
                    <option value="20:00">8:00 PM</option>
                    <option value="20:30">8:30 PM</option>
                    <option value="21:00">9:00 PM</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Number of Guests</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={editData.guests}
                  onChange={(e) => setEditData({ ...editData, guests: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Special Requests</Label>
                <Textarea
                  value={editData.special_requests}
                  onChange={(e) => setEditData({ ...editData, special_requests: e.target.value })}
                  placeholder="Any dietary requirements or special occasions?"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <Button onClick={() => setEditing(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleModify} disabled={saving} className="flex-1 btn-primary">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : cancelling ? (
          <div className="card-elegant p-8">
            <h2 className="font-serif text-xl text-gray-800 mb-4">Cancel Reservation</h2>
            <p className="text-gray-500 mb-4">Are you sure you want to cancel your reservation for {booking.date} at {booking.time}?</p>
            <div className="mb-4">
              <Label>Reason for cancellation (optional)</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Let us know why you're cancelling..."
              />
            </div>
            <div className="flex gap-4">
              <Button onClick={() => setCancelling(false)} variant="outline" className="flex-1">
                Keep Reservation
              </Button>
              <Button onClick={handleCancel} disabled={saving} className="flex-1 bg-red-500 hover:bg-red-600 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Cancellation'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="card-elegant p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-green-600 font-medium">Confirmed</span>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-malva" />
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="text-gray-800 font-medium">{booking.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Clock className="w-5 h-5 text-malva" />
                <div>
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="text-gray-800 font-medium">{booking.time}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Users className="w-5 h-5 text-malva" />
                <div>
                  <p className="text-sm text-gray-500">Guests</p>
                  <p className="text-gray-800 font-medium">{booking.guests} people</p>
                </div>
              </div>
              {booking.special_requests && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Special Requests</p>
                  <p className="text-gray-800">{booking.special_requests}</p>
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <Button onClick={() => setEditing(true)} variant="outline" className="flex-1">
                <Edit2 className="w-4 h-4 mr-2" />
                Modify
              </Button>
              <Button onClick={() => setCancelling(true)} variant="outline" className="flex-1 border-red-200 text-red-500 hover:bg-red-50">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            Need help? Contact us at{' '}
            <a href="mailto:bookings@revadining.co.uk" className="text-malva-dark hover:underline">
              bookings@revadining.co.uk
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManageBookingPage;
